import { Badge, Button, Input } from "antd";
import { Calendar, Filter, List, Search } from "lucide-react";
import { FC, useEffect, useState, useMemo } from "react";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useBooking } from "~/store";

interface BookingSearchProps {
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  viewMode: string;
  setViewMode: (mode: string) => void;
  newLeadsCount: number;
}

export const BookingSearch: FC<BookingSearchProps> = ({
  showFilters,
  setShowFilters,
  viewMode,
  setViewMode,
  newLeadsCount,
}) => {
  const { filters, setFilters } = useBooking();

  const [searchTerm, setSearchTerm] = useState("");

  const [search] = useDebounceValue(searchTerm, 500);

  useEffect(() => {
    setFilters({ search, page: 1 });
  }, [search, setFilters]);

  // Count active filters (excluding search, pagination, and default values)
  const activeFilterCount = useMemo(() => {
    if (!filters) return 0;
    let count = 0;

    // Only count filters that have actual values (not empty strings or undefined)
    if (filters.clientId && filters.clientId !== "") count++;
    if (filters.serviceType && filters.serviceType !== "") count++;
    if (filters.bookingStatus && filters.bookingStatus !== "") count++;
    if (filters.startDate && filters.startDate !== "") count++;
    if (filters.endDate && filters.endDate !== "") count++;
    if (filters.timeFilter && filters.timeFilter !== "") count++;

    // Subtract 1 to account for default filter, return 0 if result is negative
    const adjustedCount = count - 1;
    return adjustedCount > 0 ? adjustedCount : 0;
  }, [filters]);

  return (
    <div className="bg-white rounded-lg border p-4 mb-6 shadow-sm">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div className="flex-1 flex space-x-4">
          <Input
            placeholder="Search bookings by ID, client, or project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<Search size={16} className="text-gray-400" />}
            className="flex-1"
          />

          <Badge count={activeFilterCount} offset={[0, 0]}>
            <Button
              icon={<Filter size={16} />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </Badge>
        </div>
        <div className="flex items-center gap-4">
          {newLeadsCount > 0 && (
            <span className="text-sm text-amber-600">
              New Leads: <span className="font-medium">{newLeadsCount}</span>
            </span>
          )}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              className={`flex items-center space-x-1 px-3 py-1 rounded-md ${
                viewMode === "list" ? "bg-white shadow-sm" : "hover:bg-gray-200"
              }`}
              onClick={() => setViewMode("list")}
            >
              <List size={16} />
              <span>List</span>
            </button>
            <button
              className={`flex items-center space-x-1 px-3 py-1 rounded-md ${
                viewMode === "calendar"
                  ? "bg-white shadow-sm"
                  : "hover:bg-gray-200"
              }`}
              onClick={() => setViewMode("calendar")}
            >
              <Calendar size={16} />
              <span>Calendar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
